import { useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDicomServiceToken } from '@core/api/getDicomServiceToken';
import { setDicomServiceAccessToken } from '@core/api/setDicomServiceAccessToken';
import useAuthReady from '@core/hooks/useAuthReady';
import { AuthContext } from '@core/providers/AuthProvider';

export const DICOM_SERVICE_SETTINGS_QUERY = 'DicomServiceSettings';

const useDicomServiceToken = (orgId?: string, studyHash?: string) => {
  const { organization } = useContext(AuthContext);
  const { isOrgReady } = useAuthReady();

  useEffect(() => {
    const newOrganizationId = organization?.organizationId || orgId;

    if (newOrganizationId !== undefined) {
      sessionStorage.setItem('organizationId', newOrganizationId);
    }
  }, [organization?.organizationId, orgId]);

  const organizationId =
    organization?.organizationId || orgId || sessionStorage.getItem('organizationId') || undefined;

  const { data: dicomServiceTokenSettings, ...rest } = useQuery({
    queryFn: () => getDicomServiceToken(studyHash),
    // NB: `studyHash` is deliberately absent from this key even though the
    // queryFn uses it — useGetStudyTokenWithoutOrgSecurity and
    // useGetStudyAuthorization both setQueryData into this same entry. See the
    // comment block in StudyConfirmAuthWrapper for the bug that coupling caused.
    queryKey: [DICOM_SERVICE_SETTINGS_QUERY, organizationId],
    // Organization-scoped only. Both variants of this endpoint 401 for an
    // anonymous visitor, who gets a DICOM token from /studies/shared/{hash}/token
    // via useGetStudyTokenWithoutOrgSecurity instead.
    enabled: isOrgReady && organizationId !== undefined,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
    refetchInterval: (query): number | false => {
      const tokenSettings = query.state.data;

      if (!tokenSettings || !tokenSettings.token.expiresOn) {
        return false;
      }

      const expiresOn = new Date(tokenSettings.token.expiresOn).getTime();
      const now = new Date().getTime();
      const timeout = expiresOn - now;
      if (timeout <= 0) {
        // expired so try to refresh it immediately
        return 1;
      }
      return timeout;
    },
  });

  useEffect(() => {
    if (!dicomServiceTokenSettings) {
      return;
    }

    setDicomServiceAccessToken(dicomServiceTokenSettings);
  }, [dicomServiceTokenSettings]);

  return { dicomServiceTokenSettings, ...rest };
};

export default useDicomServiceToken;
